import React, { useState, useCallback, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { TopicStep, type TopicStepData } from "./wizard/TopicStep";
import { SourcesStep, type SourceItem } from "./wizard/SourcesStep";
import { OutlineStep, type CourseOutline } from "./wizard/OutlineStep";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useToast } from "@/hooks/use-toast";
import { Loader2, ArrowLeft, ArrowRight, Sparkles, RefreshCw } from "lucide-react";

interface CreateCourseWizardProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  initialStep?: number;
}

const STEP_TITLES = ["Topic & Parameters", "Add Sources", "Preview Outline"];

export const CreateCourseWizard: React.FC<CreateCourseWizardProps> = ({
  isOpen,
  onOpenChange,
  initialStep = 0,
}) => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { toast } = useToast();

  const [step, setStep] = useState(initialStep);

  // Sync step when initialStep changes (e.g. user clicks a different CTA card)
  useEffect(() => {
    if (isOpen) {
      setStep(initialStep);
    }
  }, [initialStep, isOpen]);
  const [topicData, setTopicData] = useState<TopicStepData>({
    topic: "",
    difficulty: "beginner",
    goal: "overview",
    additionalInstructions: "",
  });
  const [sources, setSources] = useState<SourceItem[]>([]);
  const [requestId, setRequestId] = useState<string | null>(null);
  const [outline, setOutline] = useState<CourseOutline | null>(null);
  const [isGeneratingOutline, setIsGeneratingOutline] = useState(false);
  const [isGeneratingCourse, setIsGeneratingCourse] = useState(false);

  // Reset state when dialog closes
  const handleOpenChange = (open: boolean) => {
    if (!open) {
      setStep(initialStep);
      setTopicData({
        topic: "",
        difficulty: "beginner",
        goal: "overview",
        additionalInstructions: "",
      });
      setSources([]);
      setRequestId(null);
      setOutline(null);
      setIsGeneratingOutline(false);
      setIsGeneratingCourse(false);
    }
    onOpenChange(open);
  };

  const addSource = (source: SourceItem) => {
    setSources((prev) => [...prev, source]);
  };

  const removeSource = (contentId: string) => {
    setSources((prev) => prev.filter((s) => s.contentId !== contentId));
  };

  const generateOutline = useCallback(async () => {
    if (!user) return;
    setIsGeneratingOutline(true);
    setOutline(null);

    try {
      const contentIds = sources.map((s) => s.contentId);
      const prompt = topicData.topic
        ? `${topicData.topic}${topicData.additionalInstructions ? `\n\nAdditional instructions: ${topicData.additionalInstructions}` : ""}`
        : null;

      const { data, error } = await supabase
        .from("course_generation_requests")
        .insert({
          user_id: user.id,
          source: sources.length > 0 ? "multi" : "prompt",
          prompt,
          content_ids: contentIds,
          difficulty: topicData.difficulty,
          goal: topicData.goal,
        })
        .select()
        .single();

      if (error) throw error;

      setRequestId(data.id);

      // Invoke the outline generation edge function
      supabase.functions
        .invoke("generate-course-outline", { body: { requestId: data.id } })
        .catch((err) => {
          console.error("Outline generation invoke failed:", err);
          setIsGeneratingOutline(false);
        });
    } catch (err: any) {
      console.error("Failed to create generation request:", err);
      toast({
        title: "Error",
        description: err?.message || "Failed to generate outline",
        variant: "destructive",
      });
      setIsGeneratingOutline(false);
    }
  }, [user, sources, topicData, toast]);

  const handleOutlineReady = useCallback((newOutline: CourseOutline) => {
    setOutline(newOutline);
    setIsGeneratingOutline(false);
  }, []);

  const handleRegenerate = useCallback(async () => {
    setOutline(null);
    setRequestId(null);
    await generateOutline();
  }, [generateOutline]);

  const handleGenerateCourse = async () => {
    if (!requestId || !outline) return;
    setIsGeneratingCourse(true);

    try {
      // Save the edited outline back
      await supabase
        .from("course_generation_requests")
        .update({
          outline: outline as any,
          updated_at: new Date().toISOString(),
        })
        .eq("id", requestId);

      // Invoke the full course generation
      supabase.functions
        .invoke("generate-course", { body: { requestId } })
        .catch((err) => console.error("Course generation invoke failed:", err));

      handleOpenChange(false);
      navigate(`/course/creating/${requestId}`);
    } catch (err: any) {
      console.error("Failed to start course generation:", err);
      toast({
        title: "Error",
        description: err?.message || "Failed to generate course",
        variant: "destructive",
      });
      setIsGeneratingCourse(false);
    }
  };

  const canGoNext = () => {
    if (step === 0) return topicData.topic.trim().length > 0;
    if (step === 1) return true; // Sources are optional
    if (step === 2) return !!outline;
    return false;
  };

  const handleNext = () => {
    if (step === 1) {
      // Moving to outline step - generate outline
      setStep(2);
      generateOutline();
    } else if (step < 2) {
      setStep(step + 1);
    }
  };

  const handleBack = () => {
    if (step > 0) setStep(step - 1);
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleOpenChange}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh] overflow-y-auto scrollbar-hide">
        <DialogHeader>
          <DialogTitle className="text-lg">{STEP_TITLES[step]}</DialogTitle>
          {/* Step indicator */}
          <div className="flex items-center gap-2 pt-2">
            {STEP_TITLES.map((_, i) => (
              <div
                key={i}
                className={`h-1 flex-1 rounded-full transition-colors ${
                  i <= step ? "bg-primary" : "bg-muted"
                }`}
              />
            ))}
          </div>
        </DialogHeader>

        <div className="py-4">
          {step === 0 && <TopicStep data={topicData} onChange={setTopicData} />}
          {step === 1 && (
            <SourcesStep
              sources={sources}
              onAddSource={addSource}
              onRemoveSource={removeSource}
            />
          )}
          {step === 2 && (
            <OutlineStep
              requestId={requestId}
              outline={outline}
              onOutlineReady={handleOutlineReady}
              onRegenerate={handleRegenerate}
              isGenerating={isGeneratingOutline}
            />
          )}
        </div>

        {/* Footer actions */}
        <div className="flex items-center justify-between pt-2">
          <div>
            {step > 0 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleBack}
                className="gap-1.5"
                disabled={isGeneratingCourse}
              >
                <ArrowLeft className="h-4 w-4" />
                Back
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            {step === 1 && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => {
                  setStep(2);
                  generateOutline();
                }}
              >
                Skip
              </Button>
            )}
            {step < 2 ? (
              <Button
                size="sm"
                onClick={handleNext}
                disabled={!canGoNext()}
                className="gap-1.5"
              >
                Next
                <ArrowRight className="h-4 w-4" />
              </Button>
            ) : (
              <>
                <Button
                  variant="outline"
                  size="sm"
                  className="gap-2 rounded-full"
                  onClick={handleRegenerate}
                  disabled={isGeneratingOutline}
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${isGeneratingOutline ? "animate-spin" : ""}`} />
                  Regenerate
                </Button>
                <Button
                  size="sm"
                  onClick={handleGenerateCourse}
                  disabled={!outline || isGeneratingCourse}
                  className="gap-1.5"
                >
                  {isGeneratingCourse ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Sparkles className="h-4 w-4" />
                  )}
                  Generate Course
                </Button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
